import { isEmpty, isNotDefined } from '@sgme/fp';
import classNames from 'classnames';
import { BatchNavigationContext } from 'components/common-form/contexts/batch/BatchNavigationContext';
import { useData } from 'components/common-form/hooks/useData';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import { useFieldTranslationKey } from 'components/common-form/hooks/useFieldTranslationKey';
import { useIsFieldInvalid } from 'components/common-form/hooks/useIsFieldInvalid';
import { usePatchedFieldHighlight } from 'components/common-form/hooks/usePatchedFieldHighlight';
import { BlockOrientationFormGroup } from 'components/shared/FormGroup';
import { useSPIntl } from 'hooks/useIntl';
import type { IntlKey } from 'locales';
import { uniqBy } from 'lodash-es';
import type { ListFieldData, ListFieldMetadata } from 'models/forms/fields';
import { ChangeEvent, FC, useCallback, useContext, useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import type { QuoteWorkspaceVersion } from 'state/userPreferences/userPreferences.model';
import type { WorkspaceType } from 'state/workspaces/model';
import { camelCaseToWords } from 'utils/string';

import type { ProductNodeBuilderProps } from '../ProductNodeBuilder.model';
import { SolvedFieldsWarnings } from './SolvedFieldsWarning';

export const ListField: FC<ProductNodeBuilderProps> = ({
  node: { id },
  workspaceType,
  quoteWorkspaceVersion,
  mustForceAdvancedMode,
  tabIndex,
}) => {
  const fieldMetadata = useFieldMetadata<ListFieldMetadata>(id);

  const { isSelected } = useContext(BatchNavigationContext);
  const translationKey = useFieldTranslationKey(id);
  const patchedFieldClassName = usePatchedFieldHighlight(id);
  const hasSolvedFieldsWarning = workspaceType === 'static' && id === 'SolvingMode';

  const { hasTooltip, minWidth, maxWidth, tooltipLocation, isReadOnly } = useMemo(() => {
    if (isNotDefined(fieldMetadata)) {
      return {};
    }
    const { hasTooltip, minWidth, maxWidth, tooltipLocation, isReadOnly } = fieldMetadata;
    return {
      hasTooltip,
      minWidth,
      maxWidth,
      tooltipLocation,
      isReadOnly,
    };
  }, [fieldMetadata]);

  const tooltipKey = hasTooltip ? (`form.${translationKey}.tooltip` as IntlKey) : undefined;
  return (
    <BlockOrientationFormGroup
      inputId={id}
      label={`form.${translationKey}` as IntlKey}
      tooltipKey={tooltipKey}
      workspaceType={workspaceType}
      minWidth={minWidth}
      maxWidth={maxWidth}
      tooltipLocation={tooltipLocation}
      className={patchedFieldClassName}
    >
      <FieldDropDownList
        id={id}
        className={classNames({ 'pe-none': isReadOnly })}
        workspaceType={workspaceType}
        quoteWorkspaceVersion={quoteWorkspaceVersion}
        mustForceAdvancedMode={mustForceAdvancedMode}
        tabIndex={tabIndex}
        isSelected={isSelected}
      />
      {hasSolvedFieldsWarning && <SolvedFieldsWarnings />}
    </BlockOrientationFormGroup>
  );
};
ListField.displayName = 'ListField';

const StaticListItem: FC<{
  code: string | undefined;
  label: string;
  translationPrefix: string;
  translationKey: string | undefined;
}> = ({ code: itemValue, label: itemLabel, translationKey, translationPrefix }) => {
  const { formatMessage } = useIntl();
  const label = useMemo(
    () =>
      itemValue === ''
        ? ''
        : itemLabel === ''
          ? ''
          : formatMessage({
              id: `${translationPrefix}${translationKey}` as any,
              defaultMessage: formatMessage({
                id: `${translationPrefix}${itemValue}` as any,
                defaultMessage: itemLabel,
              }),
            }),
    [formatMessage, itemLabel, itemValue, translationKey, translationPrefix],
  );
  return <option value={itemValue}>{label}</option>;
};

const DynamicListItem: FC<{
  code: string | undefined;
  label: string;
}> = ({ code: itemValue, label: itemLabel }) => {
  const label = useMemo(
    () => (isEmpty(itemValue) ? '' : itemLabel === '' ? camelCaseToWords(itemValue) : itemLabel),
    [itemLabel, itemValue],
  );
  return <option value={itemValue}>{label}</option>;
};

function getLabelFromCode(items: { value: string; label: string }[], value: Readonly<string | null>) {
  return items.find(o => o.value === value)?.label;
}

export interface FieldDropDownListProps {
  id: string;
  workspaceType?: WorkspaceType;
  className?: string;
  quoteWorkspaceVersion?: QuoteWorkspaceVersion;
  mustForceAdvancedMode?: boolean;
  tabIndex?: number;
  isSelected?: boolean;
  onEditionCompleted?: () => void;
}

const SORT_TRIGGER = 100;
export function FieldDropDownList({
  id,
  className,
  workspaceType,
  quoteWorkspaceVersion,
  mustForceAdvancedMode,
  tabIndex,
  isSelected,
}: FieldDropDownListProps) {
  const { formatMessage, newCollator } = useSPIntl();
  const {
    availableItems = [],
    isReadOnly,
    itemsForAdvancedMode = [],
    keepLabelStyle,
  } = useFieldMetadata<ListFieldMetadata>(id)!;
  const isInvalid = useIsFieldInvalid(id);
  const [value, setValue] = useData<ListFieldData>(id);
  const selectRef = useRef<HTMLSelectElement>(null);
  const onChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const newValue = e.target.value;
      if (newValue !== value) {
        setValue(newValue);
      }
    },
    [setValue, value],
  );
  const availableOptions = useMemo(() => {
    const standardMode = quoteWorkspaceVersion === 'Standard' && mustForceAdvancedMode === false;
    const itemsForThisMode =
      standardMode === true && itemsForAdvancedMode.length > 0
        ? availableItems.filter(item => itemsForAdvancedMode.includes(item.value) === false)
        : availableItems;

    const deduplicatedItems = uniqBy(itemsForThisMode, 'value');
    if (deduplicatedItems.length < itemsForThisMode.length) {
      throw new Error(
        `ListField<${id}> contains some bad values (duplicate) : ${JSON.stringify(itemsForThisMode.map(x => x.value))}`,
      );
    }

    const items = deduplicatedItems.map(({ value: val, label, translationKey }) => {
      if (isEmpty(label)) {
        const lab = val ?? '';
        return { value: val, translationKey, label: lab };
      }
      return {
        value: val,
        translationKey,
        label: formatMessage({
          id: `field.${label}` as any,
          defaultMessage: workspaceType === 'addonManagement' || keepLabelStyle ? label : camelCaseToWords(label),
        }),
      };
    });
    if (items.length > SORT_TRIGGER && workspaceType === 'dynamic') {
      const collator = newCollator();
      items.sort((item1, item2) => collator.compare(item1.label, item2.label));
    }
    return items;
  }, [
    quoteWorkspaceVersion,
    mustForceAdvancedMode,
    itemsForAdvancedMode,
    availableItems,
    id,
    formatMessage,
    workspaceType,
    newCollator,
    keepLabelStyle,
  ]);
  const tooltip = useMemo(() => getLabelFromCode(availableOptions, value), [availableOptions, value]);
  const showTooltip = tooltip !== undefined && tooltip.length > 20;
  const selectedLabel = useMemo(
    () => availableOptions.find(option => option.value === value)?.label,
    [availableOptions, value],
  );

  useEffect(() => {
    if (isSelected && selectRef.current !== null && onChange !== null) {
      selectRef.current.focus();
    }
  }, [isSelected, onChange]);

  const isReadOnlyInputWorkspace =
    isReadOnly && (workspaceType === 'static' || workspaceType === 'dynamic' || workspaceType === 'newBatch');

  if (isReadOnlyInputWorkspace) {
    return (
      <input
        key={`${id}${value}`}
        id={id}
        className={classNames('form-control', className, { 'is-warning': isInvalid })}
        value={selectedLabel ?? ''}
        data-e2e={`${id}-select`}
        title={showTooltip ? tooltip : undefined}
        tabIndex={tabIndex}
        readOnly
      />
    );
  }

  return (
    <select
      key={`${id}${value}`}
      id={id}
      className={classNames('form-select', className, { 'is-warning': isInvalid })}
      value={value || ''}
      onChange={onChange}
      disabled={isReadOnly}
      data-e2e={`${id}-select`}
      title={showTooltip ? tooltip : undefined}
      tabIndex={tabIndex}
      ref={selectRef}
    >
      {availableOptions.map(({ value: itemValue, translationKey: itemTranslationKey, label: itemLabel }, index) =>
        workspaceType === 'dynamic' ? (
          <DynamicListItem key={itemValue || index} code={itemValue} label={itemLabel} />
        ) : (
          <StaticListItem
            key={itemValue || index}
            code={itemValue}
            translationPrefix={`form.${id}.`}
            translationKey={itemTranslationKey}
            label={itemLabel}
          />
        ),
      )}
    </select>
  );
}

FieldDropDownList.displayName = 'FieldDropDownList';

ListField.displayName = 'ListField';
